// src/common/Documents/ActionButtons/ActionButtons.js

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { Menu, Box, Divider, CircularProgress, MenuItem, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useLanguage } from "context/LanguageContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const ActionButtons = ({
  onRecognizeUnrecognized,
  onExportXml,
  onExportExcel,
  onExportRoda,
  onUpload,
  uploading,
  uploadProgress,
  onGeneratePdfReport, // {{ Add onGeneratePdfReport prop }}
  isLoading, // {{ Add isLoading prop for PDF generation }}
  unrecognizedCount,
  selectedCount,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const exportOptions = useMemo(
    () => [
      { label: t("exportToAgnum"), value: "agnum" },
      { label: t("exportExcel"), value: "excel" },
      { label: t("exportRodaTxt") || "Export RODA TXT", value: "roda" },
      { label: t("generatePdfReport"), value: "pdf" },
    ],
    [t]
  );

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleOptionSelect = (value) => {
    console.log("Selected export type:", value);
    if (value === "excel") {
      onExportExcel();
    } else if (value === "roda") {
      if (onExportRoda) onExportRoda();
    } else if (value === "pdf") {
      if (onGeneratePdfReport) onGeneratePdfReport();
    } else {
      onExportXml(value, exportWithMatches);
    }
    handleExportClose();
  };

  const [exportWithMatches, setExportWithMatches] = useState(false);
  const recognitionLabel = `${t("recognizeUnrecognized") || t("recognize")} (${unrecognizedCount} ${
    t("onThisPage") || "šiame puslapyje"
  })`;
  const exportScopeLabel = `${t("selectedDocuments") || "Pasirinkti dokumentai"}: ${selectedCount}`;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isMdDown ? "repeat(2, minmax(0, 1fr))" : "none",
        gap: 0.75,
        marginBottom: isMdDown ? 1 : 0,
        width: isMdDown ? "100%" : "auto",
        ...(isMdDown
          ? {}
          : {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              rowGap: 0.75,
              columnGap: 0.75,
            }),
      }}
    >
      <Box sx={{ width: isMdDown ? "100%" : "auto" }}>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={onRecognizeUnrecognized}
          disabled={unrecognizedCount === 0}
          sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
        >
          {recognitionLabel}
        </MDButton>
      </Box>

      <Box sx={{ width: isMdDown ? "100%" : "auto" }}>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={handleExportClick}
          endIcon={<ArrowDropDownIcon />}
          disabled={isLoading}
          sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              {t("generating")}
            </>
          ) : (
            t("export")
          )}
        </MDButton>
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={handleExportClose}
          PaperProps={{
            sx: { minWidth: 240, mt: 0.5 },
          }}
        >
          <Box sx={{ px: 1.5, pt: 0.75, pb: 0.5 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={exportWithMatches}
                  onChange={(e) => setExportWithMatches(e.target.checked)}
                  name="exportWithMatches"
                  color="primary"
                />
              }
              label={t("exportWithMatches")}
            />
          </Box>
          <Divider sx={{ my: 0.5 }} />
          {exportOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleOptionSelect(option.value)}
              disabled={option.value !== "pdf" && selectedCount === 0}
            >
              <Box>
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.value === "pdf" ? t("generatePdfReport") : exportScopeLabel}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <MDButton
        variant="contained"
        color="info"
        size="small"
        component="label"
        disabled={!!uploading}
        sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
      >
        {uploading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            {t("uploading") || "Uploading"}
            {typeof uploadProgress === "number" ? ` ${uploadProgress}%` : ""}
          </>
        ) : (
          t("uploadDocuments") || "Upload Documents"
        )}
        <input
          type="file"
          multiple
          accept="image/*,.pdf,application/pdf"
          hidden
          onChange={onUpload}
          disabled={!!uploading}
        />
      </MDButton>
    </Box>
  );
};

ActionButtons.propTypes = {
  onRecognizeUnrecognized: PropTypes.func.isRequired,
  onExportXml: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func.isRequired,
  onExportRoda: PropTypes.func,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  onGeneratePdfReport: PropTypes.func, // {{ Add onGeneratePdfReport prop type }}
  isLoading: PropTypes.bool, // {{ Add isLoading prop type }}
  unrecognizedCount: PropTypes.number,
  selectedCount: PropTypes.number,
};

// Set default value for isLoading
ActionButtons.defaultProps = {
  isLoading: false,
  uploading: false,
  uploadProgress: undefined,
  unrecognizedCount: 0,
  selectedCount: 0,
};

export default ActionButtons;
